import { useEffect, useState } from "react";
import { Pencil, Trash2, UserPlus, Users as UsersIcon } from "lucide-react";
import type { AdminIdentity, AdminRole } from "../shared/types";
import { api, type AdminUser, errorText } from "./api";
import { Button, CompanyField, RoleSelect } from "cfw-lib-admin-ui/preact";
import { parseUserAddress } from "cfw-lib-admin-ui/user-profile";
import { Loading, Notice, PageHeading } from "./ui";

export function Users({
  identity,
  onIdentityChange,
}: {
  identity: AdminIdentity;
  onIdentityChange: (identity: AdminIdentity | null) => void;
}) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [nameEdited, setNameEdited] = useState(false);
  const [nameSourceEmail, setNameSourceEmail] = useState("");
  const [company, setCompany] = useState("");
  const [addVersion, setAddVersion] = useState(0);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [editName, setEditName] = useState("");
  const [editCompany, setEditCompany] = useState("");
  const [role, setRole] = useState<AdminRole>("editor");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    let live = true;
    api
      .users()
      .then((users) => {
        if (live) setUsers(users);
      })
      .catch((error) => {
        if (live) setError(errorText(error));
      });
    return () => {
      live = false;
    };
  }, []);
  function parseAddress() {
    try {
      const address = parseUserAddress(email);
      setEmail(address.email);
      const resolvedName = nameEdited
        ? name
        : address.name || (nameSourceEmail === address.email ? name : "");
      if (!nameEdited) setName(resolvedName);
      setNameSourceEmail(address.email);
      return { email: address.email, name: resolvedName };
    } catch (error) {
      setError(errorText(error));
      return null;
    }
  }
  async function save(
    email: string,
    role: AdminRole,
    profile?: { name?: string; company?: string },
  ) {
    setBusy(email);
    setError("");
    setNotice("");
    try {
      setUsers(await api.saveUser(email, role, profile));
      if (profile) {
        setEmail("");
        setName("");
        setNameEdited(false);
        setNameSourceEmail("");
        setCompany("");
        setAddVersion((version) => version + 1);
      }
      setNotice(`Access updated for ${email}.`);
      if (email === identity.email) onIdentityChange(await api.me());
    } catch (error) {
      setError(errorText(error));
    } finally {
      setBusy("");
    }
  }
  async function saveProfile(user: AdminUser) {
    setBusy(user.email);
    setError("");
    setNotice("");
    try {
      setUsers(
        await api.updateUserProfile(user.email, {
          name: editName,
          company: editCompany,
        }),
      );
      setEditing(null);
      setNotice(`Profile updated for ${user.email}.`);
      if (user.email === identity.email) onIdentityChange(await api.me());
    } catch (error) {
      setError(errorText(error));
    } finally {
      setBusy("");
    }
  }
  async function remove(user: AdminUser) {
    if (
      !window.confirm(
        `Remove access for ${user.email}?${user.email === identity.email ? " You will lose access to this workspace." : ""}`,
      )
    )
      return;
    setBusy(user.email);
    setError("");
    setNotice("");
    try {
      setUsers(await api.removeUser(user.email));
      setNotice(`Access removed for ${user.email}.`);
      if (user.email === identity.email) onIdentityChange(null);
    } catch (error) {
      setError(errorText(error));
    } finally {
      setBusy("");
    }
  }
  return (
    <>
      <PageHeading
        title="Users"
        description="Manage who can view, edit and publish your maps."
        className="em-page-heading"
      />
      {error && <Notice>{error}</Notice>}
      {notice && <Notice success>{notice}</Notice>}
      <div className="em-users-grid">
        <section className="em-panel em-users-panel">
          <div className="em-panel-title">
            <div>
              <h2>Your team</h2>
              <p className="gau-help">
                Members sign in with your organisation’s access provider.
              </p>
            </div>
            <UsersIcon size={21} />
          </div>
          {!users ? (
            error ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setError("");
                  api
                    .users()
                    .then(setUsers)
                    .catch((error) => setError(errorText(error)));
                }}
              >
                Try again
              </Button>
            ) : (
              <Loading label="Loading people…" />
            )
          ) : (
            <div className="em-user-list">
              <label className="gau-field em-user-search">
                Search people
                <input
                  className="gau-input"
                  type="search"
                  value={search}
                  placeholder="Name, email or company"
                  onInput={(event) => setSearch(event.currentTarget.value)}
                />
              </label>
              {users
                .filter((user) =>
                  [user.name, user.email, user.company].some((value) =>
                    value?.toLowerCase().includes(search.trim().toLowerCase()),
                  ),
                )
                .map((user) => (
                  <div className="em-user-entry" key={user.email}>
                    <div className="em-user-row">
                      <div className="em-avatar" aria-hidden="true">
                        {(user.name || user.email)[0]?.toUpperCase()}
                      </div>
                      <div className="em-user-name">
                        <strong>{user.name || user.email}</strong>
                        {user.name && <small>{user.email}</small>}
                        {user.company && <small>{user.company}</small>}
                        {user.email === identity.email && <small>You</small>}
                      </div>
                      <RoleSelect
                        label={`Role for ${user.email}`}
                        hideLabel
                        options={[
                          { value: "owner", label: "Owner" },
                          { value: "editor", label: "Editor" },
                          { value: "viewer", label: "Viewer" },
                        ]}
                        value={user.role}
                        disabled={!!busy}
                        onChange={(event) => {
                          const next = event.currentTarget.value as AdminRole;
                          if (
                            user.email === identity.email &&
                            next !== "owner" &&
                            !window.confirm(
                              "Change your own role? You may lose access to user management.",
                            )
                          ) {
                            event.currentTarget.value = user.role;
                            return;
                          }
                          void save(user.email, next);
                        }}
                      />
                      <button
                        className="em-icon-button em-edit-user"
                        type="button"
                        disabled={!!busy}
                        aria-label={`Edit profile for ${user.email}`}
                        title="Edit profile"
                        aria-expanded={editing?.email === user.email}
                        onClick={() => {
                          setEditing(
                            editing?.email === user.email ? null : user,
                          );
                          setEditName(user.name || "");
                          setEditCompany(user.company || "");
                        }}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="em-icon-button em-remove-user"
                        type="button"
                        disabled={!!busy}
                        aria-label={`Remove access for ${user.email}`}
                        title="Remove access"
                        onClick={() => void remove(user)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {editing?.email === user.email && (
                      <form
                        className="em-user-profile"
                        aria-label={`Edit profile for ${user.email}`}
                        onSubmit={(event) => {
                          event.preventDefault();
                          void saveProfile(user);
                        }}
                      >
                        <label className="gau-field">
                          Name (optional)
                          <input
                            className="gau-input"
                            value={editName}
                            maxLength={200}
                            autoComplete="name"
                            disabled={!!busy}
                            onInput={(event) =>
                              setEditName(event.currentTarget.value)
                            }
                          />
                        </label>
                        <CompanyField
                          key={user.email}
                          autoFill={false}
                          value={editCompany}
                          email={user.email}
                          disabled={!!busy}
                          onChange={setEditCompany}
                          loadSuggestions={api.companies}
                        />
                        <div className="em-actions">
                          <Button type="submit" disabled={!!busy}>
                            Save profile
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={!!busy}
                            onClick={() => setEditing(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                ))}
              {users.length === 0 && (
                <p className="gau-help">No people have been added yet.</p>
              )}
              {users.length > 0 &&
                !users.some((user) =>
                  [user.name, user.email, user.company].some((value) =>
                    value?.toLowerCase().includes(search.trim().toLowerCase()),
                  ),
                ) && <p className="gau-help">No people match your search.</p>}
            </div>
          )}
        </section>
        <div>
          <form
            className="em-panel em-access-form"
            onSubmit={(event) => {
              event.preventDefault();
              const address = parseAddress();
              if (address)
                void save(address.email, role, {
                  ...(address.name.trim() ? { name: address.name } : {}),
                  ...(company.trim() ? { company } : {}),
                });
            }}
          >
            <div className="em-panel-title">
              <div>
                <h2>Give someone access</h2>
                <p className="gau-help">
                  Enter an email address or paste Name
                  &lt;email@example.com&gt;.
                </p>
              </div>
              <UserPlus size={21} />
            </div>
            <label className="gau-field">
              Email address
              <input
                className="gau-input"
                type="text"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellcheck={false}
                required
                maxLength={512}
                value={email}
                placeholder="colleague@gsma.com"
                onInput={(event) => setEmail(event.currentTarget.value)}
                onBlur={() => {
                  if (email.trim()) parseAddress();
                }}
                disabled={!!busy}
              />
            </label>
            <label className="gau-field">
              Name (optional)
              <input
                className="gau-input"
                value={name}
                maxLength={200}
                autoComplete="name"
                disabled={!!busy}
                onInput={(event) => {
                  setName(event.currentTarget.value);
                  setNameEdited(true);
                }}
              />
            </label>
            <CompanyField
              key={addVersion}
              value={company}
              email={email}
              disabled={!!busy}
              onChange={setCompany}
              loadSuggestions={api.companies}
            />
            <RoleSelect
              label="Role"
              options={[
                { value: "editor", label: "Editor" },
                { value: "viewer", label: "Viewer" },
                { value: "owner", label: "Owner" },
              ]}
              value={role}
              onChange={(event) =>
                setRole(event.currentTarget.value as AdminRole)
              }
              disabled={!!busy}
            />
            <Button type="submit" disabled={!!busy}>
              {busy && busy === email ? "Updating access…" : "Give access"}
            </Button>
            <p className="gau-help">
              This saves access permissions; it does not send an invitation.
            </p>
          </form>
          <div className="em-role-guide">
            <h3>Choose the right access</h3>
            <dl>
              <dt>Owner</dt>
              <dd className="gau-help">
                Manage people, edit maps and publish.
              </dd>
              <dt>Editor</dt>
              <dd className="gau-help">Create, edit and publish maps.</dd>
              <dt>Viewer</dt>
              <dd className="gau-help">View maps, drafts and embed details.</dd>
            </dl>
          </div>
        </div>
      </div>
    </>
  );
}
